import { RAW_COUNTRIES } from '@/constants/countries';
import { COMPONENT_TYPES } from '@/constants/places';

import { Language } from '@/types/i18n';
import {
  AddressComponentType,
  GooglePlaceApiResponse,
  PlacePrediction,
} from '@/types/places';
import { AddressDetails } from '@/types/user';

import { getDisplayCountryName } from './user';

export const parseAddressComponents = (
  placeResponse: GooglePlaceApiResponse,
): AddressDetails => {
  const componentMap: Record<string, string> = {};
  const { addressComponents } = placeResponse;

  addressComponents.forEach(item => {
    (item.types ?? []).forEach(type => {
      if (type === AddressComponentType.Country) {
        componentMap[AddressComponentType.Country] = item.shortText ?? '';
      } else if (
        type &&
        COMPONENT_TYPES.includes(type as AddressComponentType)
      ) {
        const normalizedLong =
          typeof item.longText === 'string' ? item.longText.trim() : '';
        componentMap[type] = normalizedLong || item.shortText?.trim() || '';
      }
    });
  });

  return {
    line1: getAddressLine1(componentMap, placeResponse.formattedAddress),
    line2: getAddressLine2(componentMap),
    state: componentMap[AddressComponentType.AdministrativeAreaLevel1],
    poBoxDistrict: componentMap[AddressComponentType.AdministrativeAreaLevel1],
    city: getCity(componentMap),
    town: componentMap[AddressComponentType.Sublocality],
    zip: componentMap[AddressComponentType.PostalCode],
    country: componentMap[AddressComponentType.Country]
      ? RAW_COUNTRIES.find(
          country =>
            country.code2 === componentMap[AddressComponentType.Country],
        )?.code3 || ''
      : '',
  } as AddressDetails;
};

export function getStreetLineFromPlacesAutocomplete(
  suggestion: PlacePrediction,
): string {
  const structuredMainText =
    suggestion.structured_formatting?.main_text?.trim();
  if (structuredMainText) {
    return structuredMainText;
  }
  const descriptionFirstSegment = suggestion.description.split(',')[0]?.trim();
  return descriptionFirstSegment || suggestion.description;
}

export function getAddressLine1(
  componentByType: Record<AddressComponentType, string>,
  formattedAddress = '',
) {
  const components: string[] = [];
  [AddressComponentType.StreetNumber, AddressComponentType.Route].forEach(
    type => {
      if (componentByType[type]) {
        components.push(componentByType[type]);
      }
    },
  );
  const line1 = mergeAddressComponents(...components);
  if (line1) {
    return line1;
  }
  return formattedAddress.split(',').shift() ?? '';
}

export function getAddressLine2(
  componentByType: Record<AddressComponentType, string>,
) {
  const components: string[] = [];
  [AddressComponentType.Room, AddressComponentType.Premise].forEach(type => {
    if (componentByType[type]) {
      components.push(componentByType[type]);
    }
  });

  return mergeAddressComponents(...components);
}

export function getCity(componentByType: Record<AddressComponentType, string>) {
  return (
    componentByType[AddressComponentType.Locality] ||
    componentByType[AddressComponentType.PostalCode]
  );
}

export function mergeAddressComponents(...args: string[]) {
  const array = [...new Set(args)].filter(i => !!i);
  if (!array.length) return undefined;

  let result = '';
  array.forEach((item, index) => {
    if (!result) {
      result += item;
    } else if (array[index - 1] && /^\d+$/.test(array[index - 1])) {
      result += ` ${item}`;
    } else {
      result += `, ${item}`;
    }
  });

  return result;
}

export function getCompleteAddress(
  addressDetailed: AddressDetails,
  language: Language = Language.English,
) {
  return [
    addressDetailed.line1,
    addressDetailed.line2,
    addressDetailed.city,
    addressDetailed.state,
    addressDetailed.zip,
    getDisplayCountryName(addressDetailed.country, language),
  ]
    .filter(Boolean)
    .join(', ');
}
